import React from 'react';
import Checkbox from 'jsx/elements/inputs/Checkbox';
import FileUpload from 'jsx/elements/inputs/FileUpload';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import Warning from 'jsx/elements/messages/Warning';

import { api } from 'api';
import Modal from './Modal';
import PortfolioUpdateModalHeader from './PortfolioUpdateModalHeader';
import MonitoringInsertResult from './MonitoringInsertResult';

export default function PortfolioFileUpdateModal({ monitorId, closeModal }) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const fileRef = React.useRef(null);
  const { t } = useLocale();

  async function save() {
    dispatch({ type: 'START_SAVING' });
    const res = await api.monitor.addFileToMonitor(state.file, monitorId, state.remove);
    if (!res.error) {
      fileRef.current.removeFile();
      dispatch({ type: 'SAVE_SUCCESS', payload: res });
    } else if (res.error === '101') {
      dispatch({ type: 'SET_EMPTY_FILE_ERROR', payload: res.error });
    } else {
      dispatch({ type: 'SET_FILE_ERROR', payload: res.error });
    }
  }

  return (
    <Modal
      isOpen
      header={t('portfolioUpdateModal_file_title')}
      onCancelClick={closeModal}
      onConfirmClick={save}
      disableSubmit={state.file === null || state.fileError}
      disableActions={state.saving}
      allowClickOverlay={!state.saving}
    >
      <PortfolioUpdateModalHeader key={state.insertResult} monitorId={monitorId} />

      {state.fileError && (
        <Warning
          extraClass="gutter-top"
          messageType="alert"
          messageHeader={t('portfolioMonitoring_uploadBox_fileError')}
          messageBody={t('portfolioMonitoring_uploadBox_fileUpload_tip')}
        />
      )}
      {state.emptyFileError && (
        <Warning
          extraClass="gutter-top"
          messageType="alert"
          messageHeader={t('portfolioMonitoring_uploadBox_emptyFileError')}
          messageBody={t('portfolioMonitoring_uploadBox_emptyFileUpload_tip')}
        />
      )}

      <MonitoringInsertResult
        data={state.insertResult}
        isOpen={state.insertResult !== null}
        onClose={() => dispatch({ type: 'CLOSE_INSERT_RESULT' })}
      />

      <div className="text-semibold gutter-top">{t('portfolioUpdateModal_file_1')}</div>
      <div className="text-14" style={{ paddingLeft: 20 }}>
        {t('portfolioUpdateModal_file_1_main')}
        <FileUpload
          ref={fileRef}
          hideUploadTextAfterChosenFile
          uploadText={t('portfolioMonitoring_uploadFile_button_text')}
          onUpdate={(file) => dispatch({ type: 'SET_FILE', payload: file })}
        />
      </div>
      <div className="text-semibold gutter-top">{t('portfolioUpdateModal_file_2')}</div>
      <div className="" style={{ paddingLeft: 20 }}>
        <Checkbox
          label={t('portfolioUpdateModal_file_yesremove')}
          id="delete-regnos"
          name="delete-regnos"
          value={state.remove}
          onChange={(_, checked) => dispatch({ type: 'SET_REMOVE', payload: checked })}
        />
      </div>
      <div className="text-semibold gutter-top">{t('portfolioUpdateModal_file_3')}</div>
      <div className="text-14" style={{ paddingLeft: 20 }}>
        {t('portfolioUpdateModal_file_3_main')}

        <div>
          <a
            href={t('portfolioUpdateModal_file_3_rulesUrl')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('portfolioUpdateModal_file_3_rules')}
          </a>
        </div>
      </div>
    </Modal>
  );
}

const initialState = {
  remove: false,
  file: null,
  fileError: false,
  saving: false,
  insertResult: null,
};

function reducer(state, { type, payload }) {
  switch (type) {
    case 'SET_REMOVE':
      return { ...state, remove: payload };
    case 'SET_FILE': {
      if (payload !== null) {
        const fileName = payload.name;
        const ext = fileName.substr(fileName.lastIndexOf('.'));
        if (!ext || (ext !== '.xlsx' && ext !== '.csv' && ext !== '.txt')) {
          return { ...state, file: payload, fileError: true };
        }
      }
      return { ...state, file: payload, fileError: false };
    }
    case 'RESET':
      return initialState;
    case 'CLOSE_INSERT_RESULT':
      return { ...state, insertResult: initialState.insertResult };
    case 'START_SAVING':
      return {
        ...initialState,
        file: state.file,
        remove: state.remove,
        saving: true,
      };
    case 'SAVE_SUCCESS':
      return { ...initialState, saving: false, insertResult: payload };
    case 'SET_EMPTY_FILE_ERROR':
      return { ...initialState, emptyFileError: payload };
    case 'SET_FILE_ERROR':
      return { ...state, fileError: payload };
    default:
      return state;
  }
}
